import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Link, Stack, Tooltip, Typography } from "@mui/material";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { EditorState, convertFromRaw, convertToRaw } from "draft-js";
import { startCase, union } from "lodash";

import ApplicationProgress from "./ApplicationProgress";
import {
  applicationSelector,
  updateNotes,
  userDetailsSelector,
} from "src/store/slices/applicationFormSlice";
import {
  getCustomerRole,
  hasDocument,
  hasDriverLicenceDocument,
  hasIDDocument,
} from "src/utils/commonFunctions";
import { documentNames, rentingBoardingLiving } from "src/constants";

const NOTES_SAVE_DELAY = 5000;

const DEFAULT_NOTES = {
  blocks: [
    {
      data: {},
      depth: 0,
      entityRanges: [],
      inlineStyleRanges: [],
      key: "2vm6d",
      text: "Please state the nature of this clients business, directors background and current work/income sources: ",
      type: "default",
    },
  ],
  entityMap: {},
};

const assetDetailsItems = [
  "ageOfAsset",
  "assetType",
  "assetValue",
  "typeOfSale",
  "condition",
  "make",
  "model",
];

const declarationItems = ["one", "two", "three"];

const entityDetailsItems = [
  "emails",
  "phoneNumbers",
  "addresses",
  "industryType",
];

const customerDetailsItems = [
  "firstName",
  "lastName",
  "dateOfBirth",
  "emails",
  "phoneNumbers",
  "addresses",
  "livingArrangements",
  "IDNumber",
  "IDCardNumber",
  "IDExpiryDate",
  "driverLicence",
  "IDDocument",
];

const primaryCustomerDetailsItems = [
  "customerAssets",
  "liabilities",
  "privacy",
  "bankStatements",
];

const listFields = ["emails", "phoneNumbers", "addresses"];

// Checklist ids must match the field ids rendered in the form, e.g. `address_${customerId}`
const sectionNameAliases = {
  addresses: "address",
  emails: "email",
  phoneNumbers: "phoneNumber",
};

// Customer items that aren't a plain field on the customer object
const customerItemChecks = {
  driverLicence: hasDriverLicenceDocument,
  IDDocument: hasIDDocument,
  privacy: (customer) => hasDocument(customer, [documentNames.PRIVACY_AGREEMENT]),
  bankStatements: (customer) =>
    hasDocument(customer, [documentNames.BANK_STATEMENTS]) ||
    customer?.creditProfile?.bankstatementsLinkSent ||
    customer?.creditProfile?.bankstatementsDocID,
  livingArrangements: (customer) =>
    customer?.customerProfile?.livingArrangements,
};

const isFieldComplete = (key, value) => {
  if (!value) return false;
  if (listFields.includes(key)) return value.length > 0;
  if (value.items) return value.items.some((item) => item.value);
  return true;
};

const buildSectionProgress = (items, isItemComplete, idSuffix) => {
  const checklist = items.map((key) => {
    const name = sectionNameAliases[key] ?? key;
    return {
      id: idSuffix ? `${name}_${idSuffix}` : name,
      title: startCase(name),
      isCompleted: !!isItemComplete(key),
    };
  });
  const progress = checklist.filter((item) => item.isCompleted).length;

  return {
    checklist,
    progress,
    total: items.length,
    isComplete: progress === items.length,
  };
};

const getCustomerRequiredItems = (customer, isPrimary, excludedRequiredFields) => {
  const items = isPrimary
    ? union(customerDetailsItems, primaryCustomerDetailsItems)
    : customerDetailsItems;

  const skippedItems = [
    ...(excludedRequiredFields ?? []),
    // Renters don't need to provide assets and liabilities
    ...(rentingBoardingLiving.includes(customer?.customerProfile?.livingArrangements)
      ? ["customerAssets", "liabilities"]
      : []),
    // ID document is only required until a driver licence is uploaded
    ...(hasDriverLicenceDocument(customer) ? ["IDDocument"] : []),
  ];

  return items.filter((item) => !skippedItems.includes(item));
};

const getApplicationProgress = (application, excludedRequiredFields) => {
  const asset = buildSectionProgress(
    assetDetailsItems,
    (key) => application?.asset?.[key]
  );

  const entities = (application?.entities ?? []).map((entity) =>
    buildSectionProgress(
      entityDetailsItems,
      (key) => isFieldComplete(key, entity?.[key]),
      entity?._id
    )
  );

  const customers = (application?.customers ?? []).map((customer, i) =>
    buildSectionProgress(
      getCustomerRequiredItems(customer, i === 0, excludedRequiredFields),
      (key) =>
        customerItemChecks[key]
          ? customerItemChecks[key](customer)
          : isFieldComplete(key, customer?.[key]),
      customer?._id
    )
  );

  const declaration = buildSectionProgress(
    declarationItems,
    (key) => application?.declaration?.[key]
  );

  const sections = [asset, ...entities, ...customers, declaration];
  const completedItems = sections.reduce((acc, section) => acc + section.progress, 0);
  const requiredItems = sections.reduce((acc, section) => acc + section.total, 0);

  // Entities and customers only exist once the application has loaded
  const isLoaded = !!application?.entities && !!application?.customers;

  return {
    asset,
    entities,
    customers,
    declaration,
    percentage: isLoaded ? Math.floor((completedItems / requiredItems) * 100) : 0,
    isComplete: isLoaded && completedItems === requiredItems,
  };
};

const createEditorState = (notes) =>
  EditorState.createWithContent(convertFromRaw(notes || DEFAULT_NOTES));

const scrollToSection = (id) => {
  document
    .getElementById(id)
    ?.scrollIntoView({ behavior: "smooth", block: "start" });
};

function ProgressItem({ label, targetId, isComplete, checklist }) {
  const link = (
    <Link
      underline="hover"
      style={{
        cursor: "pointer",
        textDecoration: isComplete ? "line-through" : undefined,
      }}
      onClick={() => scrollToSection(targetId)}
    >
      {label}
    </Link>
  );

  if (!checklist) return link;

  return (
    <Tooltip
      arrow
      placement="right"
      title={checklist.map((item) => (
        <Typography
          key={item.id}
          onClick={(e) => {
            e.stopPropagation();
            scrollToSection(item.id);
          }}
          style={{
            cursor: "pointer",
            textDecoration: item.isCompleted ? "line-through" : "none",
          }}
        >
          {item.title}
        </Typography>
      ))}
    >
      {link}
    </Tooltip>
  );
}

export default function Notes({ handleApplicationComplete }) {
  const dispatch = useDispatch();
  const { excludedRequiredFields } = useSelector(userDetailsSelector);
  const application = useSelector(applicationSelector);

  const notes = application?.notes?.value;
  const customerRole = getCustomerRole(application?.entities);

  const [editorState, setEditorState] = useState(() => createEditorState(notes));
  const [unsavedNotes, setUnsavedNotes] = useState(null);

  const progress = useMemo(
    () => getApplicationProgress(application, excludedRequiredFields),
    [application, excludedRequiredFields]
  );

  useEffect(() => {
    handleApplicationComplete(progress.isComplete);
  }, [progress.isComplete, handleApplicationComplete]);

  useEffect(() => {
    setEditorState(createEditorState(notes));
  }, [notes]);

  // Saves once typing has paused; each new change restarts the timer
  useEffect(() => {
    if (!unsavedNotes) return;

    const timer = setTimeout(() => {
      dispatch(
        updateNotes({
          value: convertToRaw(unsavedNotes.editorState.getCurrentContent()),
          application: unsavedNotes.applicationId,
          _id: unsavedNotes.notesId,
        })
      );
    }, NOTES_SAVE_DELAY);

    return () => clearTimeout(timer);
  }, [unsavedNotes, dispatch]);

  const handleEditorChange = (newEditorState) => {
    // Focus and cursor moves keep the same content, so they don't need saving
    if (newEditorState.getCurrentContent() !== editorState.getCurrentContent()) {
      // Ids are captured now so a pending save still targets this application after switching
      setUnsavedNotes({
        editorState: newEditorState,
        applicationId: application?._id,
        notesId: notes?._id,
      });
    }
    setEditorState(newEditorState);
  };

  const { asset, entities, customers, declaration, percentage } = progress;

  return (
    <Card
      style={{
        margin: "0 0 10px 0",
        padding: "15px",
        width: "calc(25vw - 40px)",
        height: "calc(100vh - 140px)",
        position: "fixed",
        overflowY: "auto",
        overflowX: "hidden",
        scrollbarColor: "white rebeccapurple",
        scrollbarWidth: "thin",
      }}
    >
      <Stack>
        <Typography variant="subtitle" style={{ marginBottom: "20px" }}>
          <strong>Application ID:</strong> {application?.applicationId}
        </Typography>
      </Stack>
      <Stack style={{ marginBottom: "20px" }}>
        <Typography variant="subtitle" style={{ fontWeight: 700 }}>
          Application progress:
        </Typography>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <ApplicationProgress value={percentage} />
          <Typography variant="subtitle" style={{ marginLeft: "10px" }}>
            {percentage}%
          </Typography>
        </Stack>
        <Stack direction="column" alignItems="baseline">
          <Typography>Items remaining:</Typography>

          <ProgressItem
            label={`Asset details: ${asset.progress}/${asset.total}`}
            targetId="assetDetails"
            isComplete={asset.isComplete}
            checklist={asset.checklist}
          />

          {entities.length > 0 ? (
            application.entities.map((entity, i) => (
              <ProgressItem
                key={entity._id}
                label={`Entity ${i + 1} details: ${entities[i].progress}/${entities[i].total}`}
                targetId={`entity-${entity._id}`}
                isComplete={entities[i].isComplete}
                checklist={entities[i].checklist}
              />
            ))
          ) : (
            <ProgressItem label="Add entity" targetId="addEntity" />
          )}

          {customers.length > 0 ? (
            application.customers.map((customer, i) => (
              <ProgressItem
                key={customer._id}
                label={`${customerRole} ${i + 1} details: ${customers[i].progress}/${customers[i].total}`}
                targetId={`applicant-${customer.firstName}-${customer.lastName}`}
                isComplete={customers[i].isComplete}
                checklist={customers[i].checklist}
              />
            ))
          ) : (
            <ProgressItem label={`Add ${customerRole}`} targetId="addGuarantor" />
          )}

          <ProgressItem
            label={`Broker declaration: ${declaration.progress}/${declaration.total}`}
            targetId="declaration"
            isComplete={declaration.isComplete}
          />
        </Stack>
      </Stack>

      <Typography variant="subtitle" style={{ fontWeight: 700 }}>
        Comments/Background:
      </Typography>
      <Editor
        wrapperStyle={{
          border: "1px solid rgba(0, 0, 0, 0.23)",
          borderRadius: "4px",
          marginTop: "10px",
          padding: "0 10px",
        }}
        toolbarHidden
        editorState={editorState}
        onEditorStateChange={handleEditorChange}
      />
    </Card>
  );
}
